import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Guide data                                                          */
/* ------------------------------------------------------------------ */

interface Guide {
  href: string
  title: string
  description: string
  category: "seller" | "agent"
}

const GUIDES: Guide[] = [
  {
    href: "/guides/selling-your-house",
    title: "Selling your house",
    description:
      "A complete step-by-step guide to selling your house in the UK. From choosing an agent to exchanging contracts and moving day.",
    category: "seller",
  },
  {
    href: "/guides/estate-agent-fees",
    title: "Estate agent fees",
    description:
      "How much do estate agents charge in 2026? A full breakdown of sole agency, multi-agency, and online agent fees across the UK.",
    category: "seller",
  },
  {
    href: "/guides/how-to-choose-estate-agent",
    title: "How to choose an estate agent",
    description:
      "An 8-point checklist for choosing the right estate agent. Credentials, marketing quality, fees, communication, reviews, and red flags to avoid.",
    category: "seller",
  },
  {
    href: "/guides/online-vs-high-street-estate-agents",
    title: "Online vs high street agents",
    description:
      "A balanced comparison of online and high street estate agents. Fees, service levels, marketing, achieved prices, and when to choose each.",
    category: "seller",
  },
  {
    href: "/guides/epc-ratings-explained",
    title: "EPC ratings explained",
    description:
      "What EPC ratings mean, why they matter when selling or renting, and practical ways to improve your property\u2019s energy efficiency rating.",
    category: "seller",
  },
  {
    href: "/guides/winning-instructions",
    title: "Winning instructions",
    description:
      "A six-step guide to winning more instructions. Research, rapport, CMA, marketing presentation, fee justification, and follow-up.",
    category: "agent",
  },
  {
    href: "/guides/property-video-marketing",
    title: "Property video marketing",
    description:
      "Why video matters, the five types of property video, platform-specific guides, and eight best practices for estate agents.",
    category: "agent",
  },
  {
    href: "/guides/property-marketing-checklist",
    title: "Property marketing checklist",
    description:
      "An interactive 24-point checklist covering photography, video, copywriting, portals, social media, and post-launch review.",
    category: "agent",
  },
  {
    href: "/guides/social-media-estate-agents",
    title: "Social media for estate agents",
    description:
      "Platform-by-platform strategies for Instagram, TikTok, Facebook, LinkedIn, and YouTube. Content ideas, posting schedules, and measuring ROI.",
    category: "agent",
  },
  {
    href: "/guides/property-photography-tips",
    title: "Property photography tips",
    description:
      "How to photograph properties that sell. Lighting, composition, staging, editing, equipment, and common mistakes to avoid.",
    category: "agent",
  },
  {
    href: "/guides/marketing-rental-property",
    title: "Marketing rental properties",
    description:
      "A practical guide for letting agents and landlords. Portal listings, social media, video tours, photography, and strategies for faster lets.",
    category: "agent",
  },
  {
    href: "/guides/property-video-script-writing",
    title: "Property video script writing",
    description:
      "How to write property video scripts that stop the scroll. A five-part structure, practical tips, common mistakes, and examples.",
    category: "agent",
  },
  {
    href: "/guides/instagram-reels-estate-agents",
    title: "Instagram Reels for estate agents",
    description:
      "How to use Instagram Reels to win instructions and attract buyers. Content ideas, best practices, posting schedules, and ROI measurement.",
    category: "agent",
  },
  {
    href: "/guides/tiktok-estate-agents",
    title: "TikTok for estate agents",
    description:
      "How to use TikTok for property marketing. Content ideas, practical tips, and a strategy that works without trending dances.",
    category: "agent",
  },
  {
    href: "/guides/rightmove-listing-optimisation",
    title: "Rightmove listing optimisation",
    description:
      "How to optimise Rightmove listings for more views and enquiries. Photography, descriptions, pricing, video, and a complete checklist.",
    category: "agent",
  },
  {
    href: "/guides/market-appraisal-presentation",
    title: "Market appraisal presentation",
    description:
      "How to deliver market appraisals that win instructions. A six-step framework, differentiators, fee handling, and follow-up strategies.",
    category: "agent",
  },
  {
    href: "/guides/zoopla-vs-rightmove-vs-onthemarket",
    title: "Zoopla vs Rightmove vs OnTheMarket",
    description:
      "A comprehensive comparison of the UK's three major property portals. Traffic, fees, features, analytics, and which portal strategy fits your agency.",
    category: "agent",
  },
  {
    href: "/guides/instagram-vs-facebook-vs-tiktok-property-ads",
    title: "Instagram vs Facebook vs TikTok for property ads",
    description:
      "Which social platform is best for advertising your listings? Audience data, ad costs, content formats, and practical strategies for each.",
    category: "agent",
  },
  {
    href: "/guides/youtube-ads-vs-facebook-ads-estate-agents",
    title: "YouTube Ads vs Facebook Ads for estate agents",
    description:
      "Compare YouTube and Facebook advertising for property. Costs, targeting, ad formats, and which platform delivers the best ROI for video ads.",
    category: "agent",
  },
  {
    href: "/guides/retargeting-vs-cold-audience-property-video-ads",
    title: "Retargeting vs cold audience video ads",
    description:
      "Compare retargeting and cold audience video ads for property marketing. CTR benchmarks, cost per enquiry, and the optimal budget split.",
    category: "agent",
  },
  {
    href: "/guides/paid-video-ads-vs-organic-social-estate-agents",
    title: "Paid video ads vs organic social",
    description:
      "Should you pay for property video ads or focus on organic social media? A comparison of reach, cost, ROI, and time investment.",
    category: "agent",
  },
  {
    href: "/guides/automated-vs-manual-video-ad-campaigns-property",
    title: "Automated vs manual video ad campaigns",
    description:
      "Compare automated and manual video ad creation for property marketing. Time, cost, consistency, and scalability for estate agents.",
    category: "agent",
  },
  {
    href: "/guides/postcode-targeting-vs-behavioural-targeting-property-ads",
    title: "Postcode vs behavioural targeting for property ads",
    description:
      "Compare postcode and behavioural targeting for property video ads. Geographic precision vs intent signals, and combined strategies.",
    category: "agent",
  },
  {
    href: "/guides/cost-per-lead-video-ads-vs-traditional-property-marketing",
    title: "Cost per lead: video ads vs traditional marketing",
    description:
      "Compare the cost per lead of video ads against Rightmove, Zoopla, print, leaflets, and other traditional property marketing channels.",
    category: "agent",
  },
]

/* ------------------------------------------------------------------ */
/* Guide card                                                          */
/* ------------------------------------------------------------------ */

function GuideCard({ guide }: { guide: Guide }) {
  return (
    <Link
      href={guide.href}
      className="group flex flex-col rounded-xl border border-stone bg-white p-6 transition-all hover:border-red/30 hover:shadow-md hover:shadow-red/5"
    >
      <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-warm-grey">
        {guide.category === "seller" ? "Home sellers" : "Estate agents"}
      </p>
      <h3 className="mt-3 font-display text-lg font-bold text-charcoal transition-colors group-hover:text-red">
        {guide.title}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate">
        {guide.description}
      </p>
      <p className="mt-4 flex items-center gap-1.5 text-sm font-medium text-red">
        Read guide
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className="size-4 transition-transform group-hover:translate-x-1"
        >
          <path
            fillRule="evenodd"
            d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
            clipRule="evenodd"
          />
        </svg>
      </p>
    </Link>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function GuidesIndexPage({ meta }: { meta: SeoMeta }) {
  const sellerGuides = GUIDES.filter((g) => g.category === "seller")
  const agentGuides = GUIDES.filter((g) => g.category === "agent")

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Free Guides for Estate Agents & Home Sellers",
            description: meta.description,
            numberOfItems: GUIDES.length,
            itemListElement: GUIDES.map((guide, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: guide.title,
              description: guide.description,
              url: `https://quiteright.co.uk${guide.href}`,
            })),
          })}
        </script>
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Free guides
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Guides for agents &amp; sellers
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Expert guides for UK estate agents and home sellers. From
            winning more instructions and marketing with video to
            understanding fees and navigating the selling process.
          </p>
        </div>
      </section>

      {/* Home seller guides */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              For home sellers
              <span className="text-red">.</span>
            </h2>
            <p className="mt-2 text-sm text-slate">
              Practical guides for anyone selling a property in the UK.
            </p>
          </ScrollReveal>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sellerGuides.map((guide) => (
              <ScrollReveal key={guide.href}>
                <GuideCard guide={guide} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Agent guides */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              For estate agents
              <span className="text-red">.</span>
            </h2>
            <p className="mt-2 text-sm text-slate">
              Professional guides to help you win instructions, market
              listings, and grow your agency.
            </p>
          </ScrollReveal>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {agentGuides.map((guide) => (
              <ScrollReveal key={guide.href}>
                <GuideCard guide={guide} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              More resources
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              {
                href: "/tools",
                title: "Tools & calculators",
                desc: "Free property calculators for agents and buyers.",
              },
              {
                href: "/glossary",
                title: "Property glossary",
                desc: "Property marketing terms explained.",
              },
              {
                href: "/directory",
                title: "Agent directory",
                desc: "Browse and compare local estate agents.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Ready to market with video
            <span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight turns your listings into scroll-stopping video ads in
            under 60 seconds. More viewings, faster sales, and vendors who
            remember your name.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
